<script setup>
import { computed, defineEmits, defineProps, ref } from 'vue';
import editIcon from '@/assets/images/icons/edit-fill.png';
import trashIcon from '@/assets/images/icons/yellow-trash.png';
import cloudDownloadIcon from '@/assets/images/screening/detail/cloud-download-fill.svg';
import clipboardIcon from '@/assets/images/screening/detail/clipboard-check.svg';
import listIcon from '@/assets/images/screening/detail/clipboard-list.svg';
import ScreeningInsights from './ScreeningInsights.vue';
import chevronDownIcon from '@/assets/images/screening/detail/chevron-down.svg';
import chevronUpIcon from '@/assets/images/screening/detail/chevron-up.svg';

const props = defineProps({
	detail: { type: Object, required: true },
});

const emit = defineEmits(['send', 'edit', 'delete']);
const scoresOpen = ref(true);
const opened = ref({});

const questions = computed(() => {
	const list = props.detail?.questions || [];
	return [...list].sort((a, b) => (a.position || 0) - (b.position || 0));
});

const formatDate = (value, withTime = false) => {
	if (!value) return '';
	const text = String(value).replace('T', ' ');
	const [date, time] = text.split(' ');
	const [year, month, day] = date.split('-');
	if (!year || !month || !day) return '';
	const shown = withTime
		? `${Number(day)}/${Number(month)}/${year}`
		: `${day}/${month}/${year}`;
	const clock = time ? time.slice(0, 5) : '';
	return withTime && clock && clock !== '00:00' ? `${shown} ${clock}` : shown;
};

const staffName = (staff, label) =>
	staff?.full_name || staff?.name || label || '';

const scoreText = (score) => {
	if (score === null || score === undefined || score === '') return '';
	const number = Number(score);
	const shown = Number.isNaN(number) ? score : number;
	return `${shown} điểm`;
};

const fileUrl = (file) => {
	if (!file) return '';
	if (typeof file === 'string') return file;
	return file.url || file.file_url || file.link || file.path || '';
};

const files = computed(() =>
	Array.isArray(props.detail?.files) ? props.detail.files : []
);

const fileName = (file) => {
	if (!file || typeof file === 'string') {
		const parts = String(file || '').split('/');
		return parts[parts.length - 1] || 'Tài liệu';
	}
	return file.name || file.filename || file.file_name || 'Tài liệu';
};

const openFile = (file) => {
	const url = fileUrl(file);
	if (url) window.open(url, '_blank');
};

const createdName = computed(() =>
	staffName(props.detail?.created_by_staff, props.detail?.created_by_label)
);

const updatedName = computed(() =>
	staffName(props.detail?.updated_by_staff, props.detail?.updated_by_label)
);

const createdDate = computed(
	() => formatDate(props.detail?.inserted_at) || formatDate(props.detail?.date)
);

const updatedDate = computed(() => formatDate(props.detail?.updated_at));

const rows = computed(() => [
	{
		label: 'Thang đánh giá',
		value: props.detail?.screening_scale?.name || '-',
	},
	{ label: 'Tuổi thực', value: props.detail?.display_age || '-' },
	{ label: 'Nhận xét', value: props.detail?.note || '-' },
]);

const maxScore = computed(() => {
	const scale = props.detail?.screening_scale || {};
	const raw =
		props.detail?.max_total_score ??
		scale.max_total_score ??
		scale.max_score;
	if (raw) return Number(raw);
	if (/c\.?a\.?r\.?s/i.test(scale.name || '')) return 60;
	return 0;
});

const percent = computed(() => {
	const raw = props.detail?.percentage ?? props.detail?.percent;
	if (raw !== null && raw !== undefined && raw !== '') {
		const number = Number(raw);
		if (!Number.isNaN(number)) return Math.max(0, Math.min(100, number));
	}
	const score = Number(props.detail?.total_score);
	if (!Number.isNaN(score) && maxScore.value) {
		return Math.round((score / maxScore.value) * 1000) / 10;
	}
	return null;
});

const ringLength = 2 * Math.PI * 46;
const ringOffset = computed(() => {
	const value = percent.value || 0;
	return ringLength - (ringLength * value) / 100;
});

const conclusionText = computed(() => {
	const value = props.detail?.conclusion;
	if (!value) return '—';
	if (typeof value === 'string') return value;
	return value.label || value.name || value.title || '—';
});

const percentLabel = computed(() => {
	if (percent.value === null) return '—';
	const rounded = Math.round(percent.value * 10) / 10;
	return `${rounded}%`;
});

const ringColor = computed(() => {
	const score = Number(props.detail?.total_score);
	if (Number.isNaN(score) || score < 30) return '#20C65B';
	if (score < 36) return '#F59E0B';
	return '#F87171';
});

const toggleScores = () => {
	scoresOpen.value = !scoresOpen.value;
};

const toggleQuestion = (id) => {
	opened.value = { ...opened.value, [id]: !opened.value[id] };
};

const choicesOf = (question) =>
	question?.choices || question?.screening_scale_choices || [];

const chosen = (question, choice) => {
	const answer = question?.answer;
	if (!answer || !choice) return false;
	const id =
		answer.screening_scale_choice_id ||
		answer.choice_id ||
		answer.screening_scale_choice?.id ||
		answer.choice?.id;
	if (id) return String(id) === String(choice.id);
	return (
		answer.score !== null &&
		answer.score !== undefined &&
		Number(answer.score) === Number(choice.score)
	);
};

const answerScore = (question) => {
	const score = question?.answer?.score ?? question?.score;
	if (score === null || score === undefined || score === '') return '-';
	return scoreText(score);
};
</script>

<template>
	<div class="done">
		<section class="done__banner">
			<div class="done__banner-top">
				<h1>{{ detail.title || 'Kết quả sàng lọc' }}</h1>
				<button
					v-if="!detail.sent_at"
					type="button"
					class="done__send"
					@click="emit('send')"
				>
					Gửi tới phụ huynh
				</button>
				<span v-else class="done__sent">Đã gửi phụ huynh</span>
			</div>
			<div class="done__banner-meta">
				<p>Ngày thực hiện: {{ formatDate(detail.date) || '-' }}</p>
				<p>
					Trị liệu viên thực hiện
					<span v-if="createdName" class="done__person">{{ createdName }}</span>
				</p>
			</div>
		</section>

		<section class="done__card">
			<div class="done__card-head">
				<h2>Tiêu đề</h2>
				<div class="done__tools">
					<button type="button" aria-label="Chỉnh sửa" @click="emit('edit')">
						<img :src="editIcon" alt="" width="20" height="20" />
					</button>
					<button type="button" aria-label="Xóa" @click="emit('delete')">
						<img :src="trashIcon" alt="" width="20" height="20" />
					</button>
				</div>
			</div>
			<div
				v-for="(row, index) in rows"
				:key="row.label"
				class="done__row"
				:class="{ 'is-alt': index % 2 === 0 }"
			>
				<p>{{ row.label }}</p>
				<p>{{ row.value }}</p>
			</div>
			<div class="done__row">
				<p>Tải lên</p>
				<p>
					<span v-if="createdName" class="done__person">{{ createdName }}</span>
					<span v-if="createdDate"> - {{ createdDate }}</span>
					<span v-if="!createdName && !createdDate">-</span>
				</p>
			</div>
			<div class="done__row is-alt">
				<p>Chỉnh sửa gần nhất</p>
				<p>
					<span v-if="updatedName" class="done__person">{{ updatedName }}</span>
					<span v-if="updatedDate"> - {{ updatedDate }}</span>
					<span v-if="!updatedName && !updatedDate">-</span>
				</p>
			</div>
			<div class="done__row done__files">
				<p>Tài liệu</p>
				<div v-if="files.length" class="done__file-list">
					<button
						v-for="(file, index) in files"
						:key="file.id || index"
						type="button"
						class="done__file cursor-pointer"
						:disabled="!fileUrl(file.file)"
						@click="openFile(file.file)"
					>
						<img
							:src="cloudDownloadIcon"
							alt=""
							width="16"
							height="16"
						/>
						<span>{{ fileName(file) }}</span>
					</button>
				</div>
				<p v-else>-</p>
			</div>
		</section>

		<section class="done__result">
			<div class="done__heading">
				<span class="done__badge">
					<img :src="clipboardIcon" alt="" width="20" height="20" />
				</span>
				<h2>Kết quả đánh giá</h2>
			</div>
			<div class="done__summary-body">
				<div class="done__ring">
					<svg viewBox="0 0 120 120">
						<circle class="is-track" cx="60" cy="60" r="46" />
						<circle
							class="is-value"
							cx="60"
							cy="60"
							r="46"
							:style="{ stroke: ringColor }"
							:stroke-dasharray="ringLength"
							:stroke-dashoffset="ringOffset"
						/>
					</svg>
					<strong>{{ percentLabel }}</strong>
				</div>
				<div class="done__summary-stats">
					<div class="done__stat-stack">
						<div>
							<span>Tổng điểm</span>
							<b>{{ detail.total_score ?? '—' }} điểm</b>
						</div>
						<div>
							<span>Kết luận</span>
							<b>{{ conclusionText }}</b>
						</div>
					</div>
					<div>
						<span>Ngày thực hiện</span>
						<b>{{ formatDate(detail.date, true) || '—' }}</b>
					</div>
				</div>
			</div>

			<ScreeningInsights :source="detail" />

			<div v-if="questions.length" class="done__section">
				<div class="done__heading">
					<span class="done__badge">
						<img :src="listIcon" alt="" width="20" height="20" />
					</span>
					<h2>Bảng điểm</h2>
					<button type="button" class="done__toggle" @click="toggleScores">
						{{ scoresOpen ? 'Đóng bảng điểm' : 'Mở bảng điểm' }}
						<img
							:src="scoresOpen ? chevronUpIcon : chevronDownIcon"
							alt=""
							width="16"
							height="16"
						/>
					</button>
				</div>
				<div v-if="scoresOpen" class="done__score-list">
					<div
						v-for="(question, index) in questions"
						:key="question.id || index"
						class="done__score-item"
					>
						<button
							type="button"
							class="done__score-row"
							@click="toggleQuestion(question.id || index)"
						>
							<span>{{ index + 1 }}. {{ question.title }}</span>
							<b>{{ answerScore(question) }}</b>
							<img
								:src="chevronDownIcon"
								alt=""
								width="20"
								height="20"
								:class="{ 'is-open': opened[question.id || index] }"
							/>
						</button>
						<div
							v-if="opened[question.id || index] && choicesOf(question).length"
							class="done__choices"
						>
							<div
								v-for="choice in choicesOf(question)"
								:key="choice.id"
								class="done__choice"
								:class="{ 'is-selected': chosen(question, choice) }"
							>
								<span class="done__choice-score">{{
									scoreText(choice.score)
								}}</span>
								<span>
									<span v-if="choice.title" class="done__choice-title">{{
										choice.title
									}}</span>
									<span v-if="choice.content" class="done__choice-content">
										{{ choice.content }}
									</span>
								</span>
							</div>
						</div>
					</div>
					<div class="done__score-total">
						<span>Tổng điểm</span>
						<b>{{ detail.total_score ?? '—' }} điểm</b>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<style lang="scss" scoped>
.done {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.done__banner {
	overflow: hidden;
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24),
		0 3px 8px -1px rgba(50, 50, 71, 0.05);
}

.done__banner-top {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 24px;
	background: #fef6f0;

	h1 {
		flex: 1;
		min-width: 0;
		color: #2aa797;
		font-size: 20px;
		line-height: 28px;
		font-weight: 600;
	}
}

.done__send {
	flex-shrink: 0;
	padding: 8px 12px;
	border-radius: 4px;
	background: #f0bb4f;
	color: #344256;
	font-size: 12px;
	line-height: 16px;
	font-weight: 600;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);
}

.done__icon {
	display: inline-flex;
	flex-shrink: 0;

	img {
		width: 20px;
		height: 20px;
	}
}

.done__sent {
	flex-shrink: 0;
	color: #65758b;
	font-size: 14px;
	font-weight: 600;
}

.done__banner-meta {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 16px;
	padding: 24px;
	border-top: 1px solid #e8eef5;
	color: #344256;
	font-size: 16px;
	line-height: 24px;
}

.done__person {
	color: #6390c3;
	font-weight: 600;
}

.done__card {
	overflow: hidden;
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);
}

.done__card-head {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 24px;

	h2 {
		flex: 1;
		color: #48566a;
		font-size: 16px;
		line-height: 24px;
		font-weight: 600;
	}
}

.done__tools {
	display: flex;
	align-items: center;
	gap: 20px;

	button {
		display: inline-flex;
	}

	img {
		width: 20px;
		height: 20px;
	}
}

.done__row {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	padding: 16px 24px;
	background: #fff;

	&.is-alt {
		background: #f8fafc;
	}

	p {
		flex: 1;
		min-width: 0;
		font-size: 16px;
		line-height: 24px;
		color: #48566a;
	}

	p:first-child {
		color: #94a3b8;
		font-size: 12px;
		line-height: 16px;
		font-weight: 600;
		letter-spacing: 0.96px;
		text-transform: uppercase;
	}
}

.done__files {
	align-items: center;
}

.done__file-list {
	display: flex;
	flex: 1;
	flex-direction: column;
	align-items: flex-start;
	gap: 8px;
	min-width: 0;
}

.done__file {
	display: inline-flex;
	align-items: flex-start;
	gap: 8px;
	max-width: 100%;
	padding: 0;
	border: 0;
	background: transparent;
	color: #6390c3;
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
	text-align: left;

	&:disabled {
		cursor: default;
	}

	img {
		flex-shrink: 0;
		margin-top: 2px;
	}

	span {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

.done__split {
	display: flex;
	align-items: center;
	justify-content: center;

	span {
		padding: 0 12px;
		color: #48566a;
		font-size: 18px;
		line-height: 24px;
		font-weight: 600;
		background: #f8fafc;
	}

	&::before,
	&::after {
		content: '';
		flex: 1;
		height: 1px;
		background: #cbd5e1;
	}
}

.done__question {
	padding: 24px;
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);
}

.done__results {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.done__question-head {
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	text-align: left;

	svg {
		flex-shrink: 0;
	}

	svg.is-closed {
		transform: rotate(180deg);
	}
}

.done__question-copy {
	display: flex;
	flex: 1;
	align-items: baseline;
	gap: 8px;
	min-width: 0;
}

.done__question-title {
	color: #344256;
	font-size: 20px;
	line-height: 28px;
	font-weight: 600;
}

.done__q-score {
	color: #2aa797;
	font-size: 20px;
	line-height: 28px;
	font-weight: 600;
	white-space: nowrap;
}

.done__choices {
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-top: 24px;
}

.done__choice {
	display: flex;
	align-items: flex-start;
	gap: 24px;
	padding: 20px 24px;
	border: 1px solid #e1e7ef;
	border-radius: 8px;
	background: #fff;
	color: #344256;
	font-size: 16px;
	line-height: 24px;

	&.is-selected {
		background: #eef2ff;
		border-color: #a5b4fc;
	}
}

.done__choice-score {
	width: 36px;
	flex-shrink: 0;
	font-weight: 600;
}

.done__choice-title {
	font-weight: 600;
}

.done__choice-content {
	font-weight: 400;
}

.done__result {
	display: flex;
	flex-direction: column;
	gap: 40px;
	padding: 32px 40px 40px;
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);
}

.done__section {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.done__heading {
	display: flex;
	align-items: center;
	gap: 16px;

	h2 {
		flex: 1;
		min-width: 0;
		color: #344256;
		font-size: 20px;
		line-height: 28px;
		font-weight: 600;
	}
}

.done__badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border-radius: 100px;
	background: #6366f1;
	flex-shrink: 0;

	img {
		width: 20px;
		height: 20px;
	}
}

.done__summary-body {
	display: flex;
	align-items: center;
	gap: 60px;
	padding: 24px 0;
}

.done__ring {
	position: relative;
	width: 167px;
	height: 167px;
	flex-shrink: 0;

	svg {
		width: 167px;
		height: 167px;
		transform: rotate(-90deg);
	}

	circle {
		fill: none;
		stroke-width: 8;
	}

	.is-track {
		stroke: #e8eef5;
	}

	.is-value {
		stroke-linecap: round;
	}

	strong {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #344256;
		font-size: 32px;
		line-height: 40px;
		font-weight: 600;
		letter-spacing: -0.64px;
	}
}

.done__summary-stats {
	display: flex;
	flex: 1;
	align-items: flex-start;
	gap: 16px;
	min-width: 0;

	span {
		display: block;
		color: #344256;
		font-size: 18px;
		line-height: 24px;
	}

	b {
		display: block;
		margin-top: 4px;
		color: #344256;
		font-size: 20px;
		line-height: 28px;
		font-weight: 600;
	}
}

.done__stat-stack {
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 16px;
	min-width: 0;
}

.done__toggle {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: #0ea5e9;
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;

	img {
		width: 16px;
		height: 16px;
	}
}

.done__score-list {
	display: flex;
	flex-direction: column;
	gap: 20px;
	padding-left: 60px;
}

.done__score-item {
	padding-bottom: 16px;
	border-bottom: 1px solid #e1e7ef;
}

.done__score-row,
.done__score-total {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	width: 100%;
	text-align: left;
	color: #344256;
	font-size: 18px;
	line-height: 24px;
	font-weight: 600;

	span {
		flex: 1;
		min-width: 0;
	}

	b {
		flex-shrink: 0;
		color: #2aa797;
		font-weight: 600;
	}

	img {
		width: 20px;
		height: 20px;
		flex-shrink: 0;
		transition: transform 0.15s ease;

		&.is-open {
			transform: rotate(180deg);
		}
	}
}

@media (max-width: 800px) {
	.done {
		gap: 24px;
	}

	.done__banner-meta,
	.done__row {
		flex-direction: column;
		gap: 4px;
	}
}
</style>
